import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { US_STATES } from '../constants';

function Register() {
    const [formData, setFormData] = useState({
        email: '', password: '', first_name: '', last_name: '', phone: '',
        address_line1: '', address_city: '', address_state: 'Kentucky', address_zip: '',
        tshirt_size: 'L'
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(0); // 0: Form, 1: Verification, 2: Success
    const [vCode, setVCode] = useState('');
    const [resending, setResending] = useState(false);
    const [resendMsg, setResendMsg] = useState('');
    
    const { register, verifyEmail, resendVerification } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await register(formData);
            setStep(1);
        } catch (err) {
            console.error('Registration error:', err);
            const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to register';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        if (vCode.length !== 6) return setError('Please enter a 6-digit code');
        setError('');
        setLoading(true);
        try {
            await verifyEmail(formData.email, vCode);
            setStep(2);
            setTimeout(() => navigate('/home'), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid verification code');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        setResendMsg('');
        try {
            await resendVerification(formData.email);
            setResendMsg('Verification code resent!');
        } catch (err) {
            setError('Failed to resend code');
        } finally {
            setResending(false);
        }
    };

    return (
        <div style={styles.container}>
            {/* Header Section */}
            <div style={styles.header}>
                <div style={styles.welcomeLine}>
                    <div style={styles.line} />
                    <span style={styles.welcomeText}>JOIN THE ROAD</span>
                    <div style={styles.line} />
                </div>
                <h1 style={styles.brandTitle}>The Copper Road</h1>
                <p style={styles.tagline}>Start your Kentucky adventure today</p>
                <div style={styles.goldDiamond}>
                    <div style={styles.diamondInner} />
                </div>
            </div>

            {/* Auth Card */}
            <div style={styles.card}>
                {step === 1 ? (
                    <div style={styles.successContainer}>
                        <div style={styles.successIcon}>✉</div>
                        <h2 style={styles.cardHeader}>Verify Your Email</h2>
                        <p style={styles.successText}>
                            We've sent a 6-digit verification code to <br/>
                            <strong style={{color: '#1c1c1c'}}>{formData.email}</strong>. 
                            Please enter it below to activate your account.
                        </p>

                        {error && <div style={styles.error}>{error}</div>}
                        {resendMsg && <div style={{...styles.error, backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0'}}>{resendMsg}</div>}

                        <form onSubmit={handleVerify} style={styles.form}>
                            <div style={styles.group}>
                                <label style={styles.label}>6-DIGIT CODE</label>
                                <input
                                    type="text"
                                    maxLength="6"
                                    placeholder="000000"
                                    value={vCode}
                                    onChange={(e) => setVCode(e.target.value.replace(/\D/g, ''))}
                                    style={{...styles.input, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 'bold'}}
                                    required
                                />
                            </div>

                            <button type="submit" style={styles.button} disabled={loading}>
                                {loading ? 'Verifying...' : 'Verify & Continue →'}
                            </button>
                        </form>

                        <p style={styles.footer}>
                            Didn't get the code? <br/>
                            <button 
                                onClick={handleResend} 
                                disabled={resending}
                                style={{background: 'none', border: 'none', color: '#b8753d', fontWeight: 'bold', cursor: 'pointer', marginTop: 8}}
                            >
                                {resending ? 'Sending...' : 'Resend Verification Email'}
                            </button>
                        </p>
                    </div>
                ) : step === 2 ? (
                    <div style={styles.successContainer}>
                        <div style={{...styles.successIcon, borderColor: '#34d399', color: '#34d399'}}>✓</div>
                        <h2 style={styles.cardHeader}>Email Verified!</h2>
                        <p style={styles.successText}>
                            Your account is now active. We're taking you to the dashboard...
                        </p>
                        <div style={styles.loaderLine}>
                            <div style={styles.loaderProgress} />
                        </div>
                    </div>
                ) : (
                    <>
                        <h2 style={styles.cardHeader}>Create Account</h2>

                        {error && <div style={styles.error}>{error}</div>}

                        <form onSubmit={handleSubmit} style={styles.form}>
                            <div style={styles.row}>
                                <div style={styles.group}>
                                    <label style={styles.label}>FIRST NAME</label>
                                    <input
                                        name="first_name"
                                        onChange={handleChange}
                                        style={styles.input}
                                        required
                                    />
                                </div>
                                <div style={styles.group}>
                                    <label style={styles.label}>LAST NAME</label>
                                    <input
                                        name="last_name"
                                        onChange={handleChange}
                                        style={styles.input}
                                        required
                                    />
                                </div>
                            </div>

                            <div style={styles.group}>
                                <label style={styles.label}>EMAIL</label>
                                <input
                                    type="email"
                                    name="email"
                                    onChange={handleChange}
                                    style={styles.input}
                                    required
                                />
                            </div>

                            <div style={styles.group}>
                                <label style={styles.label}>PASSWORD</label>
                                <input
                                    type="password"
                                    name="password"
                                    onChange={handleChange}
                                    style={styles.input}
                                    required
                                    minLength="8"
                                />
                            </div>

                            {/* Optional Fields as per Requirements */}
                            <div style={styles.sectionTitle}>Optional (Required for T-Shirt)</div>

                            <div style={styles.group}>
                                <label style={styles.label}>PHONE</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.group}>
                                <label style={styles.label}>STREET ADDRESS</label>
                                <input
                                    type="text"
                                    name="address_line1"
                                    onChange={handleChange}
                                    style={styles.input}
                                />
                            </div>

                            <div style={styles.row}>
                                <div style={styles.group}>
                                    <label style={styles.label}>CITY</label>
                                    <input
                                        type="text"
                                        name="address_city"
                                        onChange={handleChange}
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.group}>
                                    <label style={styles.label}>STATE</label>
                                    <select
                                        name="address_state"
                                        onChange={handleChange}
                                        style={styles.input}
                                        value={formData.address_state}
                                    >
                                        <option value="">Select State</option>
                                        {US_STATES.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={styles.row}>
                                <div style={styles.group}>
                                    <label style={styles.label}>ZIP CODE</label>
                                    <input
                                        type="text"
                                        name="address_zip"
                                        onChange={handleChange}
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.group}>
                                    <label style={styles.label}>T-SHIRT SIZE</label>
                                    <select
                                        name="tshirt_size"
                                        onChange={handleChange}
                                        style={styles.input}
                                        defaultValue="L"
                                    >
                                        {['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <button type="submit" style={styles.button} disabled={loading}>
                                {loading ? 'Creating...' : 'Create Account →'}
                            </button>
                        </form>

                        <p style={styles.footer}>
                            Already on the road? <Link to="/login" style={styles.signLink}>Sign In</Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        minHeight: '100vh', background: '#0a0f14', padding: '60px 24px',
        fontFamily: 'Inter, system-ui, sans-serif'
    },
    header: { textAlign: 'center', marginBottom: 60, width: '100%', maxWidth: 400 },
    welcomeLine: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 15, marginBottom: 12 },
    line: { height: 1.5, background: '#8a6d3b', width: 30 },
    welcomeText: { color: '#8a6d3b', fontSize: 11, fontWeight: 'bold', letterSpacing: 2 },
    brandTitle: {
        fontSize: 48, fontFamily: 'Georgia, serif', color: '#fff',
        margin: '0 0 8px 0', fontWeight: 'bold', lineHeight: 1.1
    },
    tagline: { color: '#888', fontSize: 13, letterSpacing: 0.5 },
    goldDiamond: {
        marginTop: 35, display: 'flex', justifyContent: 'center'
    },
    diamondInner: {
        width: 32, height: 32, background: '#f59e0b', transform: 'rotate(45deg)', borderRadius: 4
    },

    card: {
        width: '100%', maxWidth: '450px', padding: '32px', backgroundColor: 'white',
        borderRadius: '30px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', textAlign: 'left'
    },
    cardHeader: {
        fontSize: '28px', color: '#1c1c1c', fontFamily: 'Georgia, serif',
        fontWeight: 'bold', marginBottom: '24px'
    },
    error: {
        backgroundColor: '#fff1f2', color: '#e11d48', padding: '12px',
        borderRadius: '8px', fontSize: '13px', marginBottom: '20px', fontWeight: '500'
    },
    sectionTitle: { fontSize: 13, fontWeight: 'bold', color: '#b8753d', letterSpacing: 1, marginTop: 12, marginBottom: 4, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 },
    form: { display: 'flex', flexDirection: 'column', gap: 20 },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
    group: { display: 'flex', flexDirection: 'column', gap: 8 },
    label: { fontSize: '10px', fontWeight: 'bold', color: '#a1a1aa', letterSpacing: 1 },
    input: {
        width: '100%', padding: '14px 16px', border: '1.2px solid #e4e4e7',
        borderRadius: '14px', fontSize: '15px', color: '#1c1c1c', outline: 'none'
    },
    button: {
        width: '100%', padding: '16px',
        background: 'linear-gradient(135deg, #b8753d 0%, #8d5d36 100%)',
        color: 'white', border: 'none', borderRadius: '40px',
        fontWeight: 'bold', fontSize: '16px', cursor: 'pointer',
        boxShadow: '0 8px 20px rgba(184,117,61,0.4)', marginTop: 10
    },
    footer: { textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#71717a' },
    signLink: { color: '#b8753d', textDecoration: 'none', fontWeight: 'bold' },

    // Success Styles
    successContainer: {
        textAlign: 'center',
        padding: '20px 0'
    },
    successIcon: {
        width: 80,
        height: 80,
        background: '#ecfdf5',
        color: '#10b981',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 40,
        margin: '0 auto 24px',
        border: '4px solid #d1fae5'
    },
    successText: {
        color: '#71717a',
        fontSize: '16px',
        lineHeight: 1.6,
        marginBottom: 32
    },
    redirectHint: {
        fontSize: '13px',
        color: '#a1a1aa',
        marginBottom: 12
    },
    loaderLine: {
        width: '100%',
        height: 4,
        background: '#f4f4f5',
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative'
    },
    loaderProgress: {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        background: '#b8753d',
        width: '100%',
        animation: 'progress 3s linear forwards'
    }
};

// Add keyframes for progress bar
const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes progress {
    0% { width: 0%; }
    100% { width: 100%; }
  }
`;
document.head.appendChild(styleSheet);

export default Register;
